import React, { useEffect, useState, useCallback } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import UserCreationDialog from '../../components/UserCreationDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Typography,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import KeyIcon from '@mui/icons-material/VpnKey';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps, createStatusChip, statusConfigs } from '../../utils/tableUtils';

export default function ManageUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ name: '', email: '', role_name: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const navigate = useNavigate();
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [emailTarget, setEmailTarget] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [userCreationDialogOpen, setUserCreationDialogOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [tenantTarget, setTenantTarget] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/accounts/admin/users/');
      const list = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setUsers(list);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch users');
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    api.get('/accounts/admin/users/tenants/').then(res => setTenants(res.data || [])).catch(() => {});
  }, [fetchUsers]);

  const openTenantDialog = (user) => {
    setTenantTarget(user);
    setSelectedTenant(user.tenant || '');
    setTenantDialogOpen(true);
  };

  const handleTenantSave = async () => {
    if (!selectedTenant) return;
    try {
      await api.patch(`/accounts/admin/users/${tenantTarget.id}/`, { tenant: selectedTenant });
      const tenantObj = tenants.find(t => t.id === selectedTenant);
      setUsers(prev => prev.map(u => u.id === tenantTarget.id ? { ...u, tenant: selectedTenant, tenant_name: tenantObj?.name || '' } : u));
      showSnackbar('Tenant updated successfully');
      setTenantDialogOpen(false);
    } catch (err) {
      showSnackbar('Failed to update tenant', 'error');
    }
  };

  const openPwdDialog = (user) => {
    setPwdTarget(user);
    setNewPwd('');
    setPwdDialogOpen(true);
  };

  const handleSetPassword = async () => {
    if (!newPwd) return;
    try {
      await api.post(`/accounts/admin/users/${pwdTarget.id}/set_password/`, { password: newPwd });
      showSnackbar('Password updated successfully');
      setPwdDialogOpen(false);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to set password', 'error');
    }
  };

  const openEmailDialog = (user) => {
    setEmailTarget(user);
    setNewEmail(user.email);
    setEmailDialogOpen(true);
  };

  const handleEmailSave = async () => {
    if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) {
      showSnackbar('Please enter a valid email', 'error');
      return;
    }
    try {
      await api.patch(`/accounts/admin/users/${emailTarget.id}/`, { email: newEmail });
      setUsers(prev => prev.map(u => u.id === emailTarget.id ? { ...u, email: newEmail } : u));
      showSnackbar('Email updated successfully');
      setEmailDialogOpen(false);
    } catch (e) {
      console.error(e);
      showSnackbar('Email update failed', 'error');
    }
  };

  const handleToggle = async (id, newActive) => {
    try {
      await api.patch(`/accounts/admin/users/${id}/`, { is_active: newActive });
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, is_active: newActive } : u)));
      showSnackbar(`User ${newActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to update user', 'error');
    }
  };

  const handleVerifyToggle = async (id, newVerified) => {
    try {
      await api.patch(`/accounts/admin/users/${id}/`, { is_verified: newVerified });
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, is_verified: newVerified } : u)));
      showSnackbar(`User ${newVerified ? 'verified' : 'unverified'} successfully`);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to update user verification status', 'error');
    }
  };

  const filteredUsers = filterData(users, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { name: '', email: '', role: '' });

  const filterConfig = [
    {
      field: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by name...'
    },
    {
      field: 'email',
      label: 'Email',
      type: 'text',
      placeholder: 'Search by email...'
    },
    {
      field: 'role_name',
      label: 'Role',
      type: 'select',
      options: [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'TEACHER', label: 'Teacher' },
        { value: 'STUDENT', label: 'Student' },
        { value: 'PARENT', label: 'Parent' }
      ]
    }
  ];

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      valueGetter: (params) => `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim() || 'N/A'
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton 
            size="small" 
            onClick={() => openEmailDialog(params.row)}
            sx={{ p: 0.5 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    },
    {
      field: 'role_name',
      headerName: 'Role',
      flex: 1,
      renderCell: (params) => {
        const chipProps = createStatusChip(params.value, {
          ADMIN: { label: 'Admin', color: 'primary' },
          TEACHER: { label: 'Teacher', color: 'secondary' },
          STUDENT: { label: 'Student', color: 'default' },
          PARENT: { label: 'Parent', color: 'info' }
        });
        return <Chip {...chipProps} />;
      }
    },
    {
      field: 'tenant_name',
      headerName: 'Tenant',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color={params.value ? 'text.primary' : 'text.secondary'}>
            {params.value || '—'}
          </Typography>
          <IconButton size="small" onClick={() => openTenantDialog(params.row)} sx={{ p: 0.5 }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const chipProps = createStatusChip(params.value ? 'active' : 'inactive', statusConfigs);
        return <Chip {...chipProps} />;
      }
    },
    {
      field: 'is_verified',
      headerName: 'Verify',
      flex: 1,
      renderCell: (params) => (
        <Switch
          checked={params.row.is_verified || false}
          onChange={(e) => handleVerifyToggle(params.row.id, e.target.checked)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => openPwdDialog(params.row)}
            sx={{ p: 0.5 }}
          >
            <KeyIcon fontSize="small" />
          </IconButton>
          <Switch
            checked={params.row.is_active}
            onChange={(e) => handleToggle(params.row.id, e.target.checked)}
            size="small"
          />
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add User',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => setUserCreationDialogOpen(true)
    }
  ];

  const emptyStateProps = {
    title: 'No users found',
    description: 'Try adjusting your filters or add your first user.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Manage Users"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredUsers.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          {...dataGridProps}
          sx={{ minWidth: 900 }}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
        </Box>
      </DataTableLayout>

      {/* Password Dialog */}
      <Dialog open={pwdDialogOpen} onClose={() => setPwdDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set New Password for {pwdTarget?.email}</DialogTitle>
        <DialogContent>
          <TextField
            type="password"
            label="New Password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            fullWidth
            autoFocus
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSetPassword} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Dialog */}
      <Dialog open={tenantDialogOpen} onClose={() => setTenantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Tenant for {tenantTarget?.email}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tenant</InputLabel>
            <Select
              value={selectedTenant}
              label="Tenant"
              onChange={(e) => setSelectedTenant(e.target.value)}
            >
              {tenants.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTenantSave} disabled={!selectedTenant}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Email for {emailTarget?.first_name} {emailTarget?.last_name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEmailSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* User Creation Dialog */}
      <UserCreationDialog
        open={userCreationDialogOpen}
        onClose={() => setUserCreationDialogOpen(false)}
        tenants={tenants}
        onSuccess={() => {
          fetchUsers(); // Refresh the users list
          showSnackbar('User created successfully!', 'success');
        }}
      />
    </AdminLayoutWrapper>
  );
}
